import { Avatar, Chip } from "@mui/material";
import Close from "@mui/icons-material/Close";
import { resolveRadiusReference } from "@/config/themeTokens";
import {
  generateLighterShade,
  generateDarkerShade,
} from "@/helpers/colorHelper";

type TagChipData = {
  id?: string | number;
  system_generated_tag_id?: string | number;
  tag_name?: string;
  channel_name?: string;
  name?: string;
  image?: string;
  color_code?: string;
  colorCode?: string;
};

type TagChipProps = {
  tag?: TagChipData;
  showRemoveIcon?: boolean;
  onRemoveTag?: (_tag?: TagChipData) => void;
  customTagStyles?: React.CSSProperties;
  [key: string]: unknown;
};

type TagChipContainerProps = {
  tags?: TagChipData[];
  [key: string]: unknown;
};

// Individual Tag Chip Component
const TagChip = ({
  tag,
  showRemoveIcon = false,
  onRemoveTag,
  customTagStyles = {},
  ...props
}: TagChipProps) => {
  const tagStyles: React.CSSProperties = {
    fontWeight: customTagStyles?.fontWeight ?? 700,
    fontSize: customTagStyles?.fontSize ?? ".7rem",
    height: customTagStyles?.height ?? 20,
    minWidth: customTagStyles?.minWidth ?? 60,
    maxWidth: customTagStyles?.maxWidth,
    width: customTagStyles?.width,
    margin: customTagStyles?.margin ?? "3px",
    borderRadius: resolveRadiusReference(
      customTagStyles?.borderRadius,
      "var(--radius-round)"
    ),
    background:
      customTagStyles?.backgroundColor ??
      generateLighterShade(tag?.color_code || tag?.colorCode),
    color:
      customTagStyles?.color ??
      generateDarkerShade(tag?.color_code || tag?.colorCode),
    cursor: customTagStyles?.cursor ?? "default",
    border: !customTagStyles?.border
      ? "none"
      : `1px solid ${tag?.color_code || tag?.colorCode}`,
  };

  return (
    <Chip
      size="small"
      label={tag?.tag_name || tag?.channel_name || tag?.name}
      style={tagStyles}
      avatar={tag?.image ? <Avatar src={tag.image} variant="square" /> : null}
      deleteIcon={
        showRemoveIcon ? (
          <Close
            fontSize="small"
            style={{
              color: generateDarkerShade(tag?.color_code || tag?.colorCode),
            }}
          />
        ) : null
      }
      onDelete={
        showRemoveIcon
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              onRemoveTag?.(tag);
            }
          : null
      }
      {...props}
    />
  );
};

// Main TagChip Container (No Expand / Collapse)
const TagChipContainer = ({ tags = [], ...props }: TagChipContainerProps) => {
  if (!tags || tags.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-start fw-b text-muted">
        {""}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "4px",
        width: "100%",
        maxWidth: "100%",
      }}
      className="p-1"
      {...props}
    >
      {tags.map((tag) => (
        <TagChip
          key={tag.id || tag.system_generated_tag_id}
          tag={tag}
          {...props}
        />
      ))}
    </div>
  );
};

export default TagChipContainer;
export { TagChip };
